import { z } from 'zod';

const JsonObjectSchema = z.record(z.string(), z.unknown());

export const InstructionTypeSchema = z.enum([
  'execute',
  'amend',
  'terminate',
  'hedge',
]);

export type InstructionType = z.infer<typeof InstructionTypeSchema>;

export const InstructionPrioritySchema = z.enum(['high', 'normal', 'low']);

export type InstructionPriority = z.infer<typeof InstructionPrioritySchema>;

export const InstructionItemSchema = z.object({
  id: z.string(),
  instruction_id: z.string(),
  status: z.string(),
  claimed_by: z.string().nullable(),
  deal_id: z.string().nullable(),
  proposed_details: JsonObjectSchema,
  execution_details: JsonObjectSchema.nullable(),
  trader_notes: z.string().nullable(),
  sequence_order: z.number().int(),
});

export type InstructionItem = z.infer<typeof InstructionItemSchema>;

export const InstructionResponseSchema = z.object({
  id: z.string(),
  instruction_type: z.string(),
  status: z.string(),
  created_by: z.string(),
  claimed_by: z.string().nullable(),
  assigned_to_book_id: z.string(),
  assigned_to_user_id: z.string().nullable(),
  notes: z.string().nullable(),
  priority: z.string().nullable(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  extensions: JsonObjectSchema.nullable(),
  items: z.array(InstructionItemSchema),
});

export type InstructionResponse = z.infer<typeof InstructionResponseSchema>;

export const CreateInstructionRequestSchema = z.object({
  instruction_type: InstructionTypeSchema,
  assigned_to_book_id: z.string().trim().min(1, 'required'),
  assigned_to_user_id: z.string().trim().min(1).optional(),
  proposed_items: z.array(JsonObjectSchema).min(1, 'required'),
  notes: z.string().optional(),
  priority: z.string().optional(),
  expires_at: z.string().optional(),
  extensions: JsonObjectSchema.optional(),
});

export type CreateInstructionRequest = z.infer<typeof CreateInstructionRequestSchema>;

export const CreateInstructionPayloadSchema = z
  .object({
    entity: z.string().optional(),
    action: z.string().optional(),
    params: JsonObjectSchema.optional(),
    instruction_type: z.string().optional(),
    assigned_to_book_id: z.string().optional(),
    assigned_to_user_id: z.string().nullable().optional(),
    proposed_items: z.array(JsonObjectSchema).optional(),
    notes: z.string().nullable().optional(),
    priority: z.string().nullable().optional(),
    expires_at: z.string().nullable().optional(),
    extensions: JsonObjectSchema.optional(),
  })
  .passthrough();

export type CreateInstructionPayload = z.infer<typeof CreateInstructionPayloadSchema>;

export const InstructionDetailPayloadSchema = z
  .object({
    instructionId: z.string().min(1).optional(),
    instruction_id: z.string().min(1).optional(),
  })
  .refine((value) => Boolean(value.instructionId ?? value.instruction_id), {
    message: 'instructionId or instruction_id is required',
  });

export type InstructionDetailPayload = z.infer<typeof InstructionDetailPayloadSchema>;
